/**
 * 🏢 AdminUsersList - Listagem Administrativa de Usuários
 * 
 * Componente para listagem completa de usuários da plataforma com:
 * - Filtros por role, status e busca
 * - Paginação
 * - Ações de edição, visualização e suspensão
 * - Estatísticas em tempo real
 * 
 * @author Pagmus Team
 * @date 2025-01-20
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Form, Row, Col, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import DataTable from 'react-data-table-component';
import AdminPageHeader from '../common/AdminPageHeader';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { API_URL } from '../../config/api';
import { Drawer } from '../../theme/components';
import StatCard from '../common/StatCard';
import ModernSwitch from '../forms/ModernSwitch';
import styles from './AdminUsersList.module.scss';

const AdminUsersList = () => {
  // Estados principais
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Estados de filtros e paginação
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Estados do drawer
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  
  // Estados para edição de taxas
  const [availablePlans, setAvailablePlans] = useState(null);
  const [taxConfig, setTaxConfig] = useState(null);
  const [togglingActive, setTogglingActive] = useState(false);
  
  // Estados para edição individual
  const [editingPlatformFee, setEditingPlatformFee] = useState(false);
  const [editingPaymentRelease, setEditingPaymentRelease] = useState(false);
  const [editingAdvancePayment, setEditingAdvancePayment] = useState(false);
  const [editingMinWithdrawal, setEditingMinWithdrawal] = useState(false);
  const [savingPlatformFee, setSavingPlatformFee] = useState(false);
  const [savingPaymentRelease, setSavingPaymentRelease] = useState(false);
  const [savingAdvancePayment, setSavingAdvancePayment] = useState(false);
  const [savingMinWithdrawal, setSavingMinWithdrawal] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUsers();
      loadStats();
    }
  }, [filters, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar lista de usuários
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/simple/users?${params}`;
      console.log('🔍 URL sendo chamada:', url);
      console.log('🔍 API_URL:', API_URL);
      console.log('🔍 Token presente:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar usuários');
      }

      if (data.success) {
        setUsers(data.data.users);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination
        }));
      } else {
        throw new Error(data.message || 'Erro ao carregar usuários');
      }

    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/simple/users/stats`;
      console.log('🔍 URL stats sendo chamada:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  };

  // Atualizar filtros
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1 // Reset para primeira página
    }));
  };

  // Navegar páginas
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Abrir drawer de detalhes
  const handleViewUser = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/simple/users/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSelectedUser(data.data);
        setShowUserDrawer(true);
        // Resetar todos os estados de edição
        setEditingPlatformFee(false);
        setEditingPaymentRelease(false);
        setEditingAdvancePayment(false);
        setEditingMinWithdrawal(false);
        // Carregar planos disponíveis e configuração de taxas
        loadAvailablePlans();
        loadUserTaxConfig(user.id);
      } else {
        throw new Error(data.message || 'Erro ao carregar detalhes do usuário');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar detalhes:', error);
      setError(error.message);
    }
  };

  // Função para obter badge de status
  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      suspended: 'danger'
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {status === 'active' ? 'Ativo' : 'Suspenso'}
      </Badge>
    );
  };

  // Função para obter badge de role
  const getRoleBadge = (role) => {
    const variants = {
      owner: 'primary',
      admin: 'warning',
      funcionario: 'info',
      customer: 'secondary'
    };
    const labels = {
      owner: 'Proprietário',
      admin: 'Administrador',
      funcionario: 'Funcionário',
      customer: 'Cliente'
    };
    return (
      <Badge bg={variants[role] || 'secondary'}>
        {labels[role] || role}
      </Badge>
    );
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Carregar planos disponíveis
  const loadAvailablePlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/team/available-plans`;
      console.log('🔍 [loadAvailablePlans] URL:', url);
      console.log('🔍 [loadAvailablePlans] API_URL:', API_URL);
      console.log('🔍 [loadAvailablePlans] Token presente:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 [loadAvailablePlans] Response status:', response.status);
      const data = await response.json();
      console.log('🔍 [loadAvailablePlans] Response data:', data);
      
      if (response.ok && data.success) {
        setAvailablePlans(data.data);
        console.log('✅ Planos carregados:', data.data);
      } else {
        console.error('❌ Erro ao carregar planos:', data.message || data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar planos:', error);
    }
  };

  // Carregar configuração de taxas do usuário
  const loadUserTaxConfig = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/team/${userId}/tax-config`;
      console.log('🔍 [loadUserTaxConfig] URL:', url);
      console.log('🔍 [loadUserTaxConfig] userId:', userId);
      console.log('🔍 [loadUserTaxConfig] API_URL:', API_URL);
      console.log('🔍 [loadUserTaxConfig] Token presente:', !!token);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 [loadUserTaxConfig] Response status:', response.status);
      const data = await response.json();
      console.log('🔍 [loadUserTaxConfig] Response data:', data);
      
      if (response.ok && data.success) {
        setTaxConfig(data.data);
        console.log('✅ Config de taxas carregada:', data.data);
      } else {
        console.error('❌ Erro ao carregar config de taxas:', data.message || data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar config de taxas:', error);
    }
  };

  // Salvar alterações individuais nas taxas
  const handleSaveTax = async (taxType, setEditingState, setSavingState) => {
    if (!selectedUser) return;
    
    try {
      setSavingState(true);
      const token = localStorage.getItem('token');
      
      // Se taxConfig for null, criar objeto vazio
      const configToSave = taxConfig || {
        platform_fee_plan_id: null,
        payment_release_plan_id: null,
        advance_payment_plan_id: null,
        minimum_withdrawal_plan_id: null
      };
      
      const response = await fetch(`${API_URL}/api/team/${selectedUser.id}/tax-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configToSave)
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setEditingState(false);
        // Recarregar configuração de taxas
        await loadUserTaxConfig(selectedUser.id);
      } else {
        throw new Error(data.message || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('❌ Erro ao salvar taxas:', error);
      setError(error.message);
    } finally {
      setSavingState(false);
    }
  };

  // Alternar status ativo/inativo do usuário
  const handleToggleActive = async () => {
    if (!selectedUser) return;
    
    try {
      setTogglingActive(true);
      const token = localStorage.getItem('token');
      const newIsActive = !selectedUser.is_active;
      
      const response = await fetch(`${API_URL}/api/team/${selectedUser.id}/toggle-active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newIsActive })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Atualizar usuário selecionado
        setSelectedUser(prev => ({
          ...prev,
          is_active: data.data.is_active
        }));
        // Recarregar lista de usuários
        loadUsers();
      } else {
        throw new Error(data.message || 'Erro ao alterar status');
      }
    } catch (error) {
      console.error('❌ Erro ao alterar status:', error);
      setError(error.message);
    } finally {
      setTogglingActive(false);
    }
  };

  return (
    <div className="admin-users-list">
      {/* Cabeçalho */}
      <div className="d-flex justify-content-between align-items-start">
        <AdminPageHeader
          icon="solar:users-group-two-rounded-bold"
          title="Gerenciamento de Usuários"
          subtitle="Gerencie todos os usuários da plataforma"
        />
        <Button variant="outline-primary" onClick={loadUsers} disabled={loading} style={{ marginTop: '8px' }}>
          <Icon icon="mdi:refresh" className="me-2" />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="row g-3" style={{ marginBottom: '24px' }}>
          <div className="col-xxl-3 col-sm-6">
            <StatCard
              variant="blue"
              title="Total de Usuários"
              value={stats.totalUsers}
              change="Usuários cadastrados na plataforma"
              icon="solar:users-group-rounded-bold"
            />
          </div>
          <div className="col-xxl-3 col-sm-6">
            <StatCard
              variant="green"
              title="Usuários Ativos"
              value={stats.activeUsers}
              change="Usuários com acesso ativo"
              icon="solar:user-check-bold"
            />
          </div>
          <div className="col-xxl-3 col-sm-6">
            <StatCard
              variant="purple"
              title="Proprietários"
              value={stats.totalOwners}
              change="Proprietários de negócios"
              icon="solar:user-star-bold"
            />
          </div>
          <div className="col-xxl-3 col-sm-6">
            <StatCard
              variant="orange"
              title="Funcionários"
              value={stats.totalEmployees}
              change="Funcionários cadastrados"
              icon="solar:user-plus-bold"
            />
          </div>
        </div>
      )}

      {/* Filtros */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>Papel</Form.Label>
                <Form.Select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <option value="all">Todos os papéis</option>
                  <option value="owner">Proprietários</option>
                  <option value="admin">Administradores</option>
                  <option value="funcionario">Funcionários</option>
                  <option value="customer">Clientes</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Ativos</option>
                  <option value="suspended">Suspensos</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>Buscar</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Nome ou email do usuário..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                  <Button variant="outline-secondary">
                    <Icon icon="mdi:magnify" />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de usuários */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <div style={{ fontSize: '14px', marginTop: '12px' }}>Carregando usuários...</div>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          ) : users.length === 0 ? (
            <div className="text-center py-4">
              <Icon icon="mdi:account-search" style={{ fontSize: '48px', color: '#6c757d' }} />
              <div style={{ fontSize: '16px', marginTop: '12px' }}>Nenhum usuário encontrado</div>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Tente ajustar os filtros de busca</div>
            </div>
          ) : (
            <DataTable
              columns={[
                {
                  name: 'Usuário',
                  selector: row => row.name,
                  sortable: true,
                  cell: row => (
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{row.name}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>{row.email}</div>
                      {row.owner_name && (
                        <div style={{ fontSize: '11px', color: '#17a2b8' }}>
                          Funcionário de: {row.owner_name}
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  name: 'Papel',
                  selector: row => row.role,
                  sortable: true,
                  cell: row => getRoleBadge(row.role)
                },
                {
                  name: 'Status',
                  selector: row => row.status,
                  sortable: true,
                  cell: row => getStatusBadge(row.status)
                },
                {
                  name: 'Vendas',
                  selector: row => row.total_sales || 0,
                  sortable: true,
                  cell: row => (
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {row.total_sales || 0}
                    </div>
                  )
                },
                {
                  name: 'Receita',
                  selector: row => row.total_revenue || 0,
                  sortable: true,
                  cell: row => (
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {CurrencyFormatter.centsToFormatted(row.total_revenue || 0)}
                    </div>
                  )
                },
                {
                  name: 'Criado em',
                  selector: row => row.created_at,
                  sortable: true,
                  cell: row => (
                    <div style={{ fontSize: '12px' }}>
                      {formatDate(row.created_at)}
                    </div>
                  )
                },
                {
                  name: 'Ações',
                  cell: row => (
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewUser(row)}
                        title="Visualizar detalhes"
                      >
                        <Icon icon="mdi:eye" />
                      </Button>
                    </div>
                  ),
                  ignoreRowClick: true,
                  allowOverflow: true,
                  button: true
                }
              ]}
              data={users}
              pagination
              paginationPerPage={pagination.limit}
              paginationTotalRows={pagination.total}
              paginationDefaultPage={pagination.page}
              onChangePage={handlePageChange}
              progressPending={loading}
              progressComponent={<Spinner animation="border" variant="primary" />}
              noDataComponent={
                <div className={styles.emptyState}>
                  <Icon icon="mdi:account-search" className={styles.emptyIcon} />
                  <div className={styles.emptyTitle}>Nenhum usuário encontrado</div>
                  <div className={styles.emptySubtitle}>Tente ajustar os filtros de busca</div>
                </div>
              }
            />
          )}
        </Card.Body>
      </Card>

      {/* Drawer de detalhes do usuário */}
      <Drawer
        show={showUserDrawer}
        onHide={() => {
          setShowUserDrawer(false);
          setSelectedUser(null);
        }}
        title={`${selectedUser?.name || 'Usuário'}`}
        subtitle="Detalhes completos do usuário"
        icon="mdi:account"
        size="lg"
        footer={
          <Drawer.Button
            variant="secondary"
            onClick={() => setShowUserDrawer(false)}
          >
            Fechar
          </Drawer.Button>
        }
      >
        {selectedUser ? (
          <>
            {/* Informações Básicas */}
            <Drawer.Section title="Informações Básicas" icon="mdi:information">
              <Drawer.Row label="Nome" value={selectedUser.name} />
              <Drawer.Row label="Email" value={selectedUser.email} />
              <Drawer.Row label="Papel" value={getRoleBadge(selectedUser.role)} />
              <Drawer.Row label="Status" value={getStatusBadge(selectedUser.status)} />
              {selectedUser.owner_name && (
                <Drawer.Row label="Funcionário de" value={selectedUser.owner_name} />
              )}
              
              {/* Status de Acesso */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
                <div>
                  <span className={styles.accessStatusLabel}>Status de Acesso</span>
                  <div className={styles.accessStatusDesc}>
                    {selectedUser.is_active ? 'Usuário pode acessar a plataforma' : 'Acesso bloqueado à plataforma'}
                  </div>
                </div>
                <ModernSwitch
                  checked={selectedUser.is_active !== false}
                  onChange={handleToggleActive}
                  disabled={togglingActive}
                  label=""
                />
              </div>
            </Drawer.Section>

            <Drawer.Divider />

            {/* Configurações de Taxas e Planos */}
            <div style={{ marginBottom: '28px' }}>
              {/* Cabeçalho da seção */}
              <div className={styles.sectionHeader}>
                <Icon icon="mdi:cash-multiple" className={styles.sectionIcon} />
                <span className={styles.sectionTitle}>
                  Configurações de Taxas e Planos
                </span>
              </div>

              {/* Conteúdo */}
              {availablePlans ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Taxa da Plataforma */}
                  <div className={styles.taxBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span className={styles.taxLabel}>
                        Taxa da Plataforma
                      </span>
                      {!editingPlatformFee && (
                        <Button 
                          variant="link" 
                          size="sm"
                          className={styles.editButton}
                          onClick={() => setEditingPlatformFee(true)}
                        >
                          <Icon icon="mdi:pencil" style={{ fontSize: '18px' }} />
                        </Button>
                      )}
                    </div>
                    {editingPlatformFee ? (
                      <>
                        <Form.Select
                          value={taxConfig?.platform_fee_plan_id || ''}
                          onChange={(e) => {
                            const newConfig = taxConfig || {};
                            setTaxConfig({ ...newConfig, platform_fee_plan_id: e.target.value ? parseInt(e.target.value) : null });
                          }}
                          style={{ marginBottom: '8px' }}
                        >
                          <option value="">Selecione um plano</option>
                          {availablePlans.platformFeePlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} {plan.is_default ? '(Padrão)' : ''} - {plan.percentage_fee}% + {CurrencyFormatter.centsToFormatted(plan.fixed_fee_cents)}
                            </option>
                          ))}
                        </Form.Select>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleSaveTax('platformFee', setEditingPlatformFee, setSavingPlatformFee)}
                            disabled={savingPlatformFee}
                          >
                            {savingPlatformFee ? <Spinner animation="border" size="sm" /> : <Icon icon="mdi:check" />}
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setEditingPlatformFee(false);
                              loadUserTaxConfig(selectedUser.id);
                            }}
                            disabled={savingPlatformFee}
                          >
                            <Icon icon="mdi:close" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.taxValue}>
                        {taxConfig?.platform_fee_name || 'Não configurado'}
                      </div>
                    )}
                  </div>

                  {/* Período de Liberação */}
                  <div className={styles.taxBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span className={styles.taxLabel}>
                        Período de Liberação
                      </span>
                      {!editingPaymentRelease && (
                        <Button 
                          variant="link" 
                          size="sm"
                          className={styles.editButton}
                          onClick={() => setEditingPaymentRelease(true)}
                        >
                          <Icon icon="mdi:pencil" style={{ fontSize: '18px' }} />
                        </Button>
                      )}
                    </div>
                    {editingPaymentRelease ? (
                      <>
                        <Form.Select
                          value={taxConfig?.payment_release_plan_id || ''}
                          onChange={(e) => {
                            const newConfig = taxConfig || {};
                            setTaxConfig({ ...newConfig, payment_release_plan_id: e.target.value ? parseInt(e.target.value) : null });
                          }}
                          style={{ marginBottom: '8px' }}
                        >
                          <option value="">Selecione um período</option>
                          {availablePlans.paymentReleasePlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} {plan.is_default ? '(Padrão)' : ''} - D+{plan.release_days}
                            </option>
                          ))}
                        </Form.Select>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleSaveTax('paymentRelease', setEditingPaymentRelease, setSavingPaymentRelease)}
                            disabled={savingPaymentRelease}
                          >
                            {savingPaymentRelease ? <Spinner animation="border" size="sm" /> : <Icon icon="mdi:check" />}
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setEditingPaymentRelease(false);
                              loadUserTaxConfig(selectedUser.id);
                            }}
                            disabled={savingPaymentRelease}
                          >
                            <Icon icon="mdi:close" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.taxValue}>
                        {taxConfig?.payment_release_name || 'Não configurado'}
                      </div>
                    )}
                  </div>

                  {/* Plano de Antecipação */}
                  <div className={styles.taxBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span className={styles.taxLabel}>
                        Plano de Antecipação
                      </span>
                      {!editingAdvancePayment && (
                        <Button 
                          variant="link" 
                          size="sm"
                          className={styles.editButton}
                          onClick={() => setEditingAdvancePayment(true)}
                        >
                          <Icon icon="mdi:pencil" style={{ fontSize: '18px' }} />
                        </Button>
                      )}
                    </div>
                    {editingAdvancePayment ? (
                      <>
                        <Form.Select
                          value={taxConfig?.advance_payment_plan_id || ''}
                          onChange={(e) => {
                            const newConfig = taxConfig || {};
                            setTaxConfig({ ...newConfig, advance_payment_plan_id: e.target.value ? parseInt(e.target.value) : null });
                          }}
                          style={{ marginBottom: '8px' }}
                        >
                          <option value="">Selecione um plano</option>
                          {availablePlans.advancePaymentPlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} {plan.is_default ? '(Padrão)' : ''} - {plan.percentage_fee}%
                            </option>
                          ))}
                        </Form.Select>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleSaveTax('advancePayment', setEditingAdvancePayment, setSavingAdvancePayment)}
                            disabled={savingAdvancePayment}
                          >
                            {savingAdvancePayment ? <Spinner animation="border" size="sm" /> : <Icon icon="mdi:check" />}
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setEditingAdvancePayment(false);
                              loadUserTaxConfig(selectedUser.id);
                            }}
                            disabled={savingAdvancePayment}
                          >
                            <Icon icon="mdi:close" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.taxValue}>
                        {taxConfig?.advance_payment_name || 'Não configurado'}
                      </div>
                    )}
                  </div>

                  {/* Valor Mínimo de Saque */}
                  <div className={styles.taxBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span className={styles.taxLabel}>
                        Valor Mínimo de Saque
                      </span>
                      {!editingMinWithdrawal && (
                        <Button 
                          variant="link" 
                          size="sm"
                          className={styles.editButton}
                          onClick={() => setEditingMinWithdrawal(true)}
                        >
                          <Icon icon="mdi:pencil" style={{ fontSize: '18px' }} />
                        </Button>
                      )}
                    </div>
                    {editingMinWithdrawal ? (
                      <>
                        <Form.Select
                          value={taxConfig?.minimum_withdrawal_plan_id || ''}
                          onChange={(e) => {
                            const newConfig = taxConfig || {};
                            setTaxConfig({ ...newConfig, minimum_withdrawal_plan_id: e.target.value ? parseInt(e.target.value) : null });
                          }}
                          style={{ marginBottom: '8px' }}
                        >
                          <option value="">Selecione um plano</option>
                          {availablePlans.minimumWithdrawalPlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} {plan.is_default ? '(Padrão)' : ''} - {CurrencyFormatter.centsToFormatted(plan.minimum_amount_cents)}
                            </option>
                          ))}
                        </Form.Select>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={() => handleSaveTax('minWithdrawal', setEditingMinWithdrawal, setSavingMinWithdrawal)}
                            disabled={savingMinWithdrawal}
                          >
                            {savingMinWithdrawal ? <Spinner animation="border" size="sm" /> : <Icon icon="mdi:check" />}
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setEditingMinWithdrawal(false);
                              loadUserTaxConfig(selectedUser.id);
                            }}
                            disabled={savingMinWithdrawal}
                          >
                            <Icon icon="mdi:close" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.taxValue}>
                        {taxConfig?.minimum_withdrawal_name || 'Não configurado'}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <div style={{ fontSize: '12px', marginTop: '8px' }}>Carregando configurações...</div>
                </div>
              )}
            </div>

            <Drawer.Divider />

            {/* Estatísticas */}
            <Drawer.Section title="Estatísticas" icon="mdi:chart-line">
              <Drawer.Row 
                label="Total de Vendas" 
                value={selectedUser.total_sales || 0}
                highlight
              />
              <Drawer.Row 
                label="Receita Total" 
                value={CurrencyFormatter.centsToFormatted(selectedUser.total_revenue || 0)}
                highlight
              />
              <Drawer.Row 
                label="Produtos" 
                value={selectedUser.total_products || 0}
              />
              <Drawer.Row 
                label="Último Login" 
                value={formatDate(selectedUser.last_login)}
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Informações de Conta */}
            <Drawer.Section title="Informações de Conta" icon="mdi:account-clock">
              <Drawer.Row 
                label="Criado em" 
                value={formatDate(selectedUser.created_at)}
              />
              <Drawer.Row 
                label="Última Atualização" 
                value={formatDate(selectedUser.updated_at)}
              />
            </Drawer.Section>
          </>
        ) : (
          <Drawer.EmptyState
            icon="mdi:alert"
            text="Não foi possível carregar os detalhes do usuário."
          />
        )}
      </Drawer>
    </div>
  );
};

export default AdminUsersList;
